import { Controller, useFormContext } from "react-hook-form";
import {
  Autocomplete,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { useParams } from "react-router";
import { Alert } from "@platform/design-system";

import { useBrokersQuery } from "../../../../../hooks/useBbe";
import { type GroupDetailsFormValues } from "../../../constants";

export const GroupDetails: React.FC = () => {
  const { id: brokerId } = useParams<{ id: string }>();
  const { control, watch, setValue } = useFormContext<GroupDetailsFormValues>();

  const businessType = watch("businessType");
  const groupType = watch("groupType");

  const { data } = useBrokersQuery(brokerId ?? "");

  return (
    <>
      <Controller
        name="businessType"
        control={control}
        rules={{ required: true }}
        render={({ field }) => (
          <FormControl>
            <FormLabel id="radio-group-business-type">
              New Business Type
            </FormLabel>
            <RadioGroup
              row
              aria-labelledby="radio-group-business-type"
              value={field.value}
              onChange={(e) => {
                field.onChange(e.target.value);
                if (e.target.value === "BOR" && groupType === "existingGroup") {
                  setValue("groupType", null);
                  setValue("groupId", null);
                }
              }}
              {...field}
            >
              <FormControlLabel
                value="newSale"
                control={<Radio color="brandPrimary" />}
                label="New Sale"
              />
              <FormControlLabel
                value="BOR"
                control={<Radio color="brandPrimary" />}
                label="BOR"
              />
            </RadioGroup>

            <FormHelperText>
              How is the new business being acquired?
            </FormHelperText>
          </FormControl>
        )}
      />

      {businessType === "newSale" && (
        <Controller
          name="groupType"
          control={control}
          rules={{ required: businessType === "newSale" }}
          render={({ field }) => (
            <FormControl>
              <FormLabel id="radio-group-group-type">Group Type</FormLabel>
              <RadioGroup
                row
                aria-labelledby="radio-group-group-type"
                {...field}
                value={field.value}
                onChange={(e) => {
                  const value = e.target.value;
                  field.onChange(value);
                  if (value === "newGroup") {
                    setValue("groupId", null);
                  } else {
                    setValue("groupName", "");
                  }
                }}
              >
                <FormControlLabel
                  value="newGroup"
                  control={<Radio color="brandPrimary" />}
                  label="New Group"
                />
                <FormControlLabel
                  value="existingGroup"
                  control={<Radio color="brandPrimary" />}
                  label="Existing Group"
                />
              </RadioGroup>

              {field.value === "existingGroup" && (
                <FormHelperText>
                  How is the new business being acquired?
                </FormHelperText>
              )}
            </FormControl>
          )}
        />
      )}

      {(businessType === "BOR" || groupType === "newGroup") && (
        <Controller
          name="groupName"
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              value={field.value}
              fullWidth
              label="Group Name"
              error={!!fieldState.error}
              helperText={
                fieldState.error?.message ?? "What is the name of your group?"
              }
              onChange={(e) => field.onChange(e.target.value)}
            />
          )}
        />
      )}

      {groupType === "existingGroup" && businessType === "newSale" && (
        <Controller
          name="groupId"
          control={control}
          render={({ field, fieldState }) => (
            <Autocomplete
              disableClearable
              options={data?.groups ?? []}
              slotProps={{
                listbox: {
                  sx: { maxHeight: { xs: 200 } },
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Group"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                />
              )}
              getOptionLabel={(option) =>
                `${option.groupName} - ${option.groupNumber}`
              }
              value={
                data?.groups?.find(
                  (g) => g.groupNumber === field.value?.brokerSk,
                ) ?? null
              }
              onChange={(_, newValue) => {
                if (newValue) {
                  field.onChange({
                    brokerName: newValue.groupName,
                    brokerSk: newValue.groupNumber,
                  });
                }
              }}
              isOptionEqualToValue={(option, val) =>
                option.groupNumber === val.groupNumber
              }
            />
          )}
        />
      )}

      <Alert severity="info" title="Group Numbers">
        A temporary group number will be assigned to all new coverages added to
        this group.
      </Alert>
    </>
  );
};
